import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGameStore } from "../store/gameStore";
import { useAuthStore } from "../store/authStore";
import { useLobbyConnection } from "../hooks/useLobby";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Badge, Button, Input, Panel, Spinner } from "../components/ui";
import { wsActions } from "../lib/ws";

export function WaitingRoomScreen() {
  const { code = "" } = useParams();
  useLobbyConnection(code);
  const state = useGameStore((s) => s.gameState);
  const lastError = useGameStore((s) => s.lastError);
  const meId = useAuthStore((s) => s.userId);
  const navigate = useNavigate();
  const [rename, setRename] = useState("");

  // Forward every player to the game screen while a match is running (the
  // server pushes the new state to the whole room on `start`). Without this
  // the game is unreachable from the UI (F1 fix). ENDED deliberately does
  // NOT forward: after a match guests can walk back here ("Torna alla lobby"
  // on the winner screen) and wait for the host to start a new one.
  useEffect(() => {
    if (state && state.game_status === "PLAYING") {
      navigate(`/game/${state.code}`, { replace: true });
    }
  }, [state, navigate]);

  const elo = useQuery({ queryKey: ["elo", "me"], queryFn: () => api.myElo(), enabled: !!meId });

  const players = useMemo(() => state?.players ?? [], [state]);
  const ownerId = state?.owner_id;
  const isOwner = meId === ownerId;

  if (!state) {
    return (
      <div className="flex h-[60vh] flex-col items-center justify-center gap-4 px-4 text-center">
        <div className="flex items-center text-ink-dim">
          <Spinner className="h-6 w-6 mr-2" /> Connessione alla lobby…
        </div>
        {lastError && (
          <div className="w-full max-w-sm rounded-lg border border-crimson-dim bg-crimson-dim/10 px-4 py-3 text-sm text-crimson">
            <p>{lastError}</p>
            <button
              className="mt-3 w-full rounded-md border border-line px-3 py-1.5 text-xs text-ink-dim hover:text-ink"
              onClick={() => navigate("/lobby")}
            >
              ← Torna al salone
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl text-ink">{state.lobby_name}</h1>
          <p className="text-sm text-ink-dim">di {state.creator_name}</p>
        </div>
        <Badge tone="brass"><span className="font-mono tracking-wider">{state.code}</span></Badge>
      </div>

      <Panel className="mb-5">
        <p className="mb-2 text-center text-sm text-ink-dim">Codice lobby — condividilo con gli amici</p>
        <div className="text-center font-mono text-4xl tracking-[0.25em] text-brass">{state.code}</div>
        <p className="mt-2 text-center text-xs text-ink-faint">Le lobby private non appaiono nella lista pubblica.</p>
      </Panel>

      <Panel className="mb-5">
        <h2 className="label-caps mb-4">Giocatori ({players.length}/8)</h2>
        <ul className="flex flex-col gap-2">
          {players.map((p) => (
            <li key={p.id} className="flex items-center justify-between rounded-lg border border-line/60 bg-surface-2/40 px-3 py-2.5">
              <div className="flex items-center gap-2.5">
                <span className={`h-2 w-2 rounded-full ${p.connected ? "bg-emerald" : "bg-ink-faint"}`} />
                <span className="text-sm text-ink">{p.name}</span>
                {p.id === ownerId && <Badge tone="brass">host</Badge>}
              </div>
              {isOwner && p.id !== meId && (
                <button className="btn-danger px-2.5 py-1 text-xs" onClick={() => wsActions.kick(p.id)}>
                  Espelli
                </button>
              )}
            </li>
          ))}
        </ul>
      </Panel>

      <Panel className="mb-5">
        <h2 className="label-caps mb-3">Cambia nome</h2>
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (rename.trim()) wsActions.rename(rename.trim()); setRename(""); }}>
          <Input value={rename} maxLength={30} onChange={(e) => setRename(e.target.value)} placeholder="Nuovo nome…" />
          <Button variant="ghost" type="submit">OK</Button>
        </form>
      </Panel>

      {state.game_status === "ENDED" && (
        <div className="mb-4 rounded-lg border border-line/60 bg-surface-2/40 px-4 py-3 text-center text-sm text-ink-dim">
          La partita è conclusa.{" "}
          {isOwner
            ? "Avvia una nuova partita quando vuoi."
            : "Attendi che l'host avvii una nuova partita."}
        </div>
      )}

      <div className="flex items-center justify-between">
        {elo.data && (
          <span className="text-sm text-ink-dim">Il tuo ELO: <span className="font-mono text-brass">{elo.data.rating}</span></span>
        )}
        {/* At ENDED the host's primary action is the rematch reset (back to
            LOBBY keeping the roster); a plain Start would be rejected by the
            server (A.Start requires LOBBY). */}
        <Button
          onClick={() => (state.game_status === "ENDED" ? wsActions.reset() : wsActions.start())}
          disabled={!isOwner || players.length < 2}
        >
          {isOwner
            ? state.game_status === "ENDED" ? "Nuova partita" : "Inizia partita"
            : "Solo l'host può iniziare"}
        </Button>
      </div>

      {/* Explicit leave: the server removes us from the roster (host passes
          to the next player; if we were the last, the lobby is deleted). The
          session socket stays connected — we just go back to the lobby
          browser without logging out (logout stays on the header "Esci"). */}
      <div className="mt-8 text-center">
        <button
          className="btn-ghost px-4 py-2 text-xs text-ink-dim hover:text-crimson"
          onClick={() => {
            wsActions.leave(state.lobby_id);
            useGameStore.getState().setGameState(null);
            navigate("/lobby");
          }}
        >
          Esci dalla lobby
        </button>
      </div>
    </div>
  );
}
